import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle2, XCircle, PartyPopper, Sparkles, Heart, ChevronDown, ChevronUp, Phone, Mail, MapPin, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { AllergyFormCompact } from '../components/AllergyFormCompact';
import { UnifiedRsvpForm } from '../components/UnifiedRsvpForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Studio contact (matches the footer / About page).
const STUDIO_CONTACT = {
  phone: '905-616-7617',
  phone_tel: '+19056167617',
  email: 'info@creativebirthdays.ca',
  address_lines: [
    'Inside Edible Arrangements',
    '1500 Upper Middle Rd W, Unit 4',
    'Oakville, Ontario L6M 3G3',
  ],
  // Pre-baked Google Maps directions URL (works with no API key).
  directions_url:
    'https://www.google.com/maps/dir/?api=1&destination=' +
    encodeURIComponent('Creative Birthdays, 1500 Upper Middle Rd W Unit 4, Oakville, ON L6M 3G3'),
};

const Rsvp = () => {
  const { token } = useParams();
  const location = useLocation();
  const deepLinkToAllergy = /\/allergy\/?$/i.test(location.pathname);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [response, setResponse] = useState(null); // 'yes' | 'no' | null
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Allergy form state
  const [showAllergyForm, setShowAllergyForm] = useState(false);
  const [allergyForm, setAllergyForm] = useState(null);
  const [allergyCompleted, setAllergyCompleted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [didRespond, setDidRespond] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API_URL}/api/public/rsvp/${token}`),
      axios.get(`${API_URL}/api/public/rsvp/${token}/allergy-form`),
    ]).then(([rsvpRes, allergyRes]) => {
      if (rsvpRes.status === 'fulfilled') {
        setData(rsvpRes.value.data);
        const existing = rsvpRes.value.data?.recipient?.rsvp_response;
        if (existing) {
          setResponse(existing);
          setMessage(rsvpRes.value.data.recipient.rsvp_message || '');
          setSubmitted(true);
          setDidRespond(true);
        }
      } else {
        setError(rsvpRes.reason?.response?.data?.detail || 'Invitation link is invalid or has expired');
      }
      if (allergyRes.status === 'fulfilled') {
        setAllergyForm(allergyRes.value.data?.form || null);
        setAllergyCompleted(!!allergyRes.value.data?.recipient?.allergy_form_completed);
      }
      setLoaded(true);
    });
  }, [token]);

  const submit = async (resp) => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/rsvp/${token}`, { response: resp, message });
      setResponse(resp);
      setSubmitted(true);
      toast.success(`Thanks — your RSVP has been recorded!`);
      // Auto-reveal allergy form when the guest said YES
      if (resp === 'yes') {
        setShowAllergyForm(true);
        setTimeout(() => {
          document.getElementById('rsvp-allergy-block')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit RSVP');
    } finally { setSubmitting(false); }
  };

  const submitAllergy = async (payload) => {
    try {
      await axios.post(`${API_URL}/api/public/rsvp/${token}/allergy-form`, payload);
      setAllergyForm({
        ...payload,
        medical_data: {
          allergies: payload.allergies,
          allergy_other: payload.allergy_other,
          medical_conditions: payload.medical_conditions,
          medical_other: payload.medical_other,
        },
      });
      setAllergyCompleted(true);
      toast.success('Thanks — we have everything we need for your child!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save — please try again');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFFDF8] flex items-center justify-center p-6">
        <div className="text-center max-w-md" data-testid="rsvp-error">
          <div className="text-6xl mb-3">😅</div>
          <h1 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Oops!</h1>
          <p className="text-sm text-[#6B6B7A] mt-2">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#FFFDF8] flex items-center justify-center"><p className="text-[#6B6B7A]">Loading…</p></div>;

  const { invitation: inv, template, recipient } = data;
  const ord = (n) => {
    const v = parseInt(n, 10) || 0;
    if (v % 100 >= 11 && v % 100 <= 13) return 'th';
    return ['th','st','nd','rd'][v % 10] || 'th';
  };

  return (
    <div className="min-h-screen p-4 sm:p-8" style={{ background: template.bg_gradient }} data-testid="rsvp-page">
      <div className="max-w-xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6 sm:p-9 text-center shadow-xl"
          data-testid="rsvp-card"
        >
          <div className="text-5xl mb-1">{template.icon}</div>
          <p className="text-xs text-[#6B6B7A]">{template.tagline}</p>
          <h1 className="text-3xl font-bold mt-2" style={{ color: template.accent, fontFamily: 'Fredoka' }} data-testid="rsvp-headline">
            You're Invited!
          </h1>

          {inv.photo_url && (
            <img src={inv.photo_url} alt="" className="mx-auto mt-5 rounded-2xl max-h-56 object-cover border-4 border-white shadow" />
          )}

          <h2 className="text-2xl font-semibold mt-5 text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }} data-testid="rsvp-subject">
            {inv.child_name}'s {inv.age}{ord(inv.age)} Birthday
          </h2>
          <div className="mt-3 inline-block text-left text-sm text-[#1E1E24] space-y-1">
            <p>📅 <strong>{inv.party_date}</strong></p>
            <p>⏰ <strong>{inv.party_time}</strong></p>
            <p>📍 <strong>{inv.location}</strong></p>
          </div>
          {inv.message && (
            <div
              className="cb-invite-message mt-4 text-sm text-[#1E1E24] bg-[#FFFDF8] rounded-xl p-4 max-w-md mx-auto"
              data-testid="rsvp-message-html"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(inv.message || '') }}
            />
          )}

          {/* Creative Birthdays contact card — dashed border + warm gradient */}
          <div
            className="mt-6 mx-auto max-w-md rounded-2xl border-2 border-dashed p-4 sm:p-5 text-left"
            style={{
              borderColor: `${template.accent}88`,
              background:
                'linear-gradient(135deg, #FFF4D6 0%, #FFE9EE 50%, #FFF4D6 100%)',
            }}
            data-testid="rsvp-contact-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4" style={{ color: template.accent }} />
              <p
                className="font-semibold text-sm text-[#1E1E24]"
                style={{ fontFamily: 'Fredoka' }}
              >
                Creative Birthdays
              </p>
            </div>
            <ul className="space-y-2 text-[13px] text-[#1E1E24]">
              <li className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 flex-shrink-0" style={{ color: template.accent }} />
                <a
                  href={`tel:${STUDIO_CONTACT.phone_tel}`}
                  className="hover:underline"
                  data-testid="rsvp-contact-phone"
                >
                  {STUDIO_CONTACT.phone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 flex-shrink-0" style={{ color: template.accent }} />
                <a
                  href={`mailto:${STUDIO_CONTACT.email}`}
                  className="hover:underline break-all"
                  data-testid="rsvp-contact-email"
                >
                  {STUDIO_CONTACT.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: template.accent }} />
                <span className="leading-snug" data-testid="rsvp-contact-address">
                  {STUDIO_CONTACT.address_lines.map((l, i) => (
                    <React.Fragment key={i}>
                      {l}
                      {i < STUDIO_CONTACT.address_lines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </span>
              </li>
            </ul>
            <a
              href={STUDIO_CONTACT.directions_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-1.5 w-full sm:w-auto rounded-full px-4 py-2 text-xs font-semibold text-white transition-transform hover:scale-[1.02]"
              style={{ background: template.accent }}
              data-testid="rsvp-directions-btn"
            >
              <Navigation className="w-3.5 h-3.5" /> Get directions on Google Maps
            </a>
          </div>

          {/* Unified RSVP + consent + waiver flow */}
          {loaded && (
            <UnifiedRsvpForm
              token={token}
              guestName={recipient?.name || ''}
              birthdayChild={inv.child_name || ''}
              accent={template.accent}
              existingRsvp={response}
              existingForm={allergyForm}
              allergyCompleted={allergyCompleted}
              onDone={(r) => { setDidRespond(true); setResponse(r); }}
            />
          )}

          {/* Soft CTA: plan your own party (after any response) */}
          {didRespond && (
            <div className="mt-5 pt-4 border-t border-dashed" style={{ borderColor: `${template.accent}55` }} data-testid="plan-your-own-party-cta">
              <p className="text-xs text-[#6B6B7A] mb-2">
                <Sparkles className="w-3 h-3 inline mr-0.5" style={{ color: template.accent }} />
                Loved this? Throw your own Creative Birthdays party.
              </p>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await axios.post(`${API_URL}/api/public/rsvp/${token}/cta-click`).catch(() => {});
                  } finally {
                    window.location.href = `/book?ref=invite_${token}`;
                  }
                }}
                className="inline-flex items-center text-sm font-semibold underline hover:no-underline"
                style={{ color: template.accent }}
                data-testid="cta-plan-own-party"
              >
                Plan your own party →
              </button>
            </div>
          )}

          <p className="text-[10px] text-[#6B6B7A] mt-7">Sent with 💌 via Creative Birthdays</p>
        </motion.div>
      </div>
    </div>
  );
};

export default Rsvp;
